import React from 'react';
import HoverTooltip from '../shared/HoverTooltip';
import { NEW_ROW_CLASS, SeverityChip, UNASSIGNED_CLASS, severityOf } from './ClientRow';
import { FLAG } from './AttentionFinding';

// The Clients list's one legend: the info icon beside the view switch. It explains the
// label, the rail and the right-hand side of a row ONCE, instead of repeating an icon
// on every row.
//
// Every chip and rail swatch here is produced by the SAME severityOf() / <SeverityChip>
// the rows use, from a sample row that lands in that state — so the colours in this
// tooltip are the colours on the rows, by construction, and cannot drift.

// Which sample row lands in which state, and what that state means. The thresholds
// (10 / 3 / 1) are the ones flagLevel() applies; `quiet` mirrors the Gone Quiet tab's
// 90-day cut.
export const SEVERITY_LEGEND = [
  { key: 'critical', sample: { brokenPromises: 10 }, means: FLAG.critical.rule },
  { key: 'high', sample: { brokenPromises: 3 }, means: FLAG.high.rule },
  { key: 'at-risk', sample: { brokenPromises: 1 }, means: FLAG.watch.rule },
  { key: 'waiting', sample: { population: 'attention', waitingOnThem: 1 }, means: 'Waiting on the client to reply or approve; nothing broken or overdue.' },
  { key: 'needs-help', sample: { population: 'attention', needsHelp: 1 }, means: 'Flagged as needing help to move forward; nothing broken or overdue.' },
  { key: 'quiet', sample: { population: 'quiet' }, means: 'Nothing flagged, but no request in 90 days or more.' },
  { key: 'steady', sample: { population: 'steady' }, means: 'Nothing flagged and still in contact.' },
];

// The three views and their counts. They partition the roster (Needs Attention wins over
// Gone Quiet), so the counts add up to every client — the page says so too.
const VIEW_COUNTS = [
  ['Needs Attention', 'clients with something leaking or waiting: a broken promise, overdue work, something waiting on the client, or a help flag.'],
  ['Gone Quiet', 'nothing flagged, but no request in 90 days or more.'],
  ['Steady', 'everyone else.'],
];

// The numbers a row's headline can lead with.
const NUMBERS = [
  ['Broken promises', 'commitments we made and missed (a running tally).'],
  ['Overdue', 'open work past its due date.'],
  ['Waiting on them', 'waiting on the client to reply or approve.'],
  ['Need help', 'flagged as needing help.'],
  ['Open', 'work currently open for the client.'],
];

/** The tooltip's content. Exported so a test can render it without hovering. */
export function LegendContent() {
  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1.5 font-semibold">The label and the rail on each row</p>
        <ul className="space-y-2">
          {SEVERITY_LEGEND.map(({ key, sample, means }) => {
            const severity = severityOf(sample);
            return (
              <li key={key} data-legend={key} className="flex items-start gap-2">
                {/* The rail, in the row's own rail colour. */}
                <span aria-hidden="true" data-testid={`legend-rail-${key}`} className={`mt-0.5 h-5 shrink-0 border-l-4 ${severity.rail}`} />
                <span className="w-[7rem] shrink-0"><SeverityChip severity={severity} /></span>
                <span className="min-w-0 flex-1">{means}</span>
              </li>
            );
          })}
        </ul>
      </div>
      {/* Two columns under the label table keep the tooltip short enough to fit a laptop
          screen — it is pointer-transparent, so it cannot scroll. */}
      <div className="grid gap-x-5 gap-y-3 border-t border-white/10 pt-3 md:grid-cols-2">
        <div>
          <p className="mb-1.5 font-semibold">Reading a row</p>
          <ul className="list-disc space-y-1.5 pl-4 marker:text-gray-500">
            <li>The rail and the label show urgency: red costs the most to ignore, amber is next, cyan is waiting on the client, gray is calm. The list is ordered by this — most broken promises first — not alphabetically.</li>
            <li>Each row is one sentence about the client: its main signal — broken promises, then overdue, then who is waiting, then open work — and how long since it last asked for something.</li>
            <li>On the right: <span data-testid="legend-unassigned" className={UNASSIGNED_CLASS}>Unassigned</span> when the client has no account owner. Click anywhere on a row to open the client.</li>
          <li><span data-testid="legend-new" className={`rounded px-1 ${NEW_ROW_CLASS}`}>A tinted row</span> is new — a client that just became flagged. The tint clears when you open it, or after 10 minutes. <span className="font-semibold">↑ N new</span> beside the views means more have arrived; click it to show them.</li>
          </ul>
        </div>
        <div>
          <p className="mb-1.5 font-semibold">What the numbers mean</p>
          <ul className="list-disc space-y-1.5 pl-4 marker:text-gray-500">
            {VIEW_COUNTS.map(([name, text]) => (
              <li key={name} data-legend-view={name}><span className="font-semibold">{name}</span> — {text}</li>
            ))}
            <li>The three views never overlap, so their counts add up to every client.</li>
            {NUMBERS.map(([name, text]) => (
              <li key={name}><span className="font-semibold">{name}</span> — {text}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** The info icon. Hover or keyboard focus opens the legend; there is no text label. */
export default function ClientsLegend() {
  return (
    <HoverTooltip
      content={<LegendContent />}
      widthClassName="w-[46rem]"
      triggerClassName="inline-flex items-center"
    >
      <button
        type="button"
        aria-label="How to read these"
        className="inline-flex h-7 w-7 cursor-help items-center justify-center rounded-full text-gray-500 transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:text-gray-400 dark:hover:text-gray-200"
      >
        <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>
    </HoverTooltip>
  );
}
